// @ts-nocheck

import { createSelector } from 'reselect';

import { Target } from '../../client';
import { isBetshareEnabled } from '../../constants/features';
import { MyMatchShortErrorLabels } from '../../constants/mymatch';
import { __ } from '../../i18n/l10n';
import {
  selectBasketCachedMMAPIResponsesInfos,
  selectBasketFreebets,
  selectBasketIsCashout,
  selectBasketItems,
  selectBasketMode,
} from '../../selectors/basket/basketPrimary.selector';
import {
  selectBasketShowResume,
  selectBasketShouldShowMainBetConfirmation,
} from '../../selectors/basketCommon.selector';
import { getNbItems, selectBasketEnhancedItem } from '../../selectors/basketItems.selector';
import { selectEntitiesBets } from '../../selectors/entities/bets/entitiesBetsPrimary.selector';
import { selectEntitiesMatches } from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOutcomes } from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { MatchEntity } from '../../states/entities/matches';
import { BasketItemStatuses, BasketModes } from '../../types/basket';
import { CardModes } from '../../types/common';
import { MarketIds } from '../../types/market';
import { SportIds } from '../../types/sports';
import { createShallowEqualSelector } from '../../utils/reselect';

const emptyObject = {};

const makeBasketCardPropsSelector = () => {
  const getCardAlertContent = createSelector([selectBasketMode, selectBasketEnhancedItem], (mode, item) => {
    const { status, betslipId, additionalMessage, error } = item;
    const isStatusWaiting = status === BasketItemStatuses.WAITING;
    const isStatusValidated = status === BasketItemStatuses.VALIDATED;
    const isStatusError = status === BasketItemStatuses.REJECTED || status === BasketItemStatuses.ERROR;

    if (mode === BasketModes.SIMPLE && (isStatusValidated || isStatusWaiting)) {
      let content = '';
      const singleText = __('Single').toLocaleLowerCase();
      if (isStatusWaiting) {
        content = __('Pending %1$s bets', singleText);
      } else {
        content = __('Bet validated!');
        const betslipReferenceParts = betslipId ? betslipId.split('-') : [];
        const finalBetslipReference = betslipReferenceParts.length > 1 ? betslipReferenceParts[1] : betslipId;
        if (finalBetslipReference) {
          content += ` - ${finalBetslipReference}`;
        }
        if (additionalMessage) {
          content += ` - ${additionalMessage}`;
        }
      }
      return content;
    }
    if (isStatusError) {
      return error?.estr;
    }
    return null;
  });

  const getItemMatch = createSelector(
    [selectBasketEnhancedItem, selectEntitiesMatches],
    (item, matches) => matches[item?.matchId],
  );

  const getItemBet = createSelector([selectBasketEnhancedItem, selectEntitiesBets], (item, bets) => bets[item?.betId]);

  const getItemOutcome = createSelector(
    [selectBasketEnhancedItem, selectEntitiesOutcomes],
    (item, outcomes) => outcomes[item?.oddId],
  );

  const getBoostedOddCardMode = createSelector([getItemMatch, selectEntitiesBets], (match, bets) => {
    if (match && match.sportId === SportIds.SPECIAL_ODDS) {
      const bet = bets[match.mainBetId];

      if (bet?.marketId === MarketIds.MARKET_ID_BOOSTED_BET) {
        return CardModes.BOOSTED;
      }

      if (bet?.marketId === MarketIds.MARKET_ID_BIG_BOOSTED_BET) {
        return CardModes.BIG_BOOSTED;
      }

      if (bet?.marketId === MarketIds.MARKET_ID_XMAS_BOOSTED_BET) {
        return CardModes.XMAS_BOOSTED;
      }
    }

    return CardModes.NORMAL;
  });

  const selectItemAttributes = createSelector([selectBasketEnhancedItem], (item) => {
    const { sportId: itemSportId, matchId: itemMatchId, eligibleFreebets, ...rest } = item;

    return {
      itemSportId,
      itemMatchId,
      eligibleFreebets: eligibleFreebets ?? emptyObject,
      ...rest,
    };
  });

  const selectMyMatchErrorLabel = createSelector(
    [selectBasketCachedMMAPIResponsesInfos, selectBasketEnhancedItem, selectBasketItems],
    (cachedMMAPIResponsesInfos, item, basketItems) => {
      const { matchId: itemMatchId, betbuilder: isItemMyMatch } = item;
      const nbSimpleMatchItemsInBasket = Object.keys(basketItems).reduce(
        (acc, itemId) =>
          basketItems[itemId].matchId === itemMatchId && !basketItems[itemId].betbuilder ? acc + 1 : acc,
        0,
      );

      if (isItemMyMatch || nbSimpleMatchItemsInBasket > 1) {
        const mmCacheByMatchId = cachedMMAPIResponsesInfos[itemMatchId];
        const errorId = mmCacheByMatchId?.result?.mymatchError?.errorId;

        if (errorId) {
          return MyMatchShortErrorLabels[errorId]();
        }
      }
    },
  );

  const selectItemMatchAttributes = createSelector([getItemMatch], (match) => ({
    sportId: match?.sportId,
    categoryId: match?.categoryId,
    status: match?.status,
    matchId: match?.matchId,
  }));

  const selectBetAttributes = createSelector([getItemBet], (bet) => ({
    betTitle: bet?.betTitle?.replace('|', ' - '),
    previousOdd: bet?.previousOdd,
  }));

  const selectOutcomeLabel = createSelector([getItemOutcome], (outcome) => outcome?.label);

  const selectSelectedFreebetId = createSelector([selectBasketItems, selectItemAttributes], (basketItems, item) => {
    const { oddId } = item;
    return basketItems?.[oddId]?.selectedFreebetId;
  });

  const selectTitle = createSelector([getItemMatch, getBoostedOddCardMode], (itemMatch, cardMode) => {
    let title = itemMatch?.title || itemMatch?.matchTitle;

    if (cardMode !== CardModes.NORMAL) {
      const splittedTitle = title.split(':');
      const splittedTitleLength = splittedTitle.length;

      if (splittedTitleLength > 1) {
        return splittedTitle.slice(1).join('').trim();
      }

      title = splittedTitle.join('').trim();
    }

    return title;
  });

  return createShallowEqualSelector(
    [
      selectItemAttributes,
      selectBasketMode,
      selectBasketIsCashout,
      getCardAlertContent,
      selectBasketShowResume,
      getNbItems,
      selectItemMatchAttributes,
      selectBetAttributes,
      selectOutcomeLabel,
      getBoostedOddCardMode,
      selectBasketFreebets,
      selectSelectedFreebetId,
      selectBasketShouldShowMainBetConfirmation,
      selectMyMatchErrorLabel,
      selectTitle,
    ],
    (
      item,
      basketMode,
      basketIsCashout,
      alertContent,
      readOnly,
      nbItem,
      match,
      bet,
      outcomeLabel,
      cardMode,
      freebets,
      selectedFreebetId,
      isShowingMainBetConfirmation,
      myMatchErrorLabel,
      title,
    ) => {
      const {
        itemSportId,
        itemMatchId,
        oddId,
        status,
        cashout,
        banker,
        validatedValue,
        value,
        selectionItems,
        potentialGain,
        betslipId,
        changed,
        eligibleFreebets,
      } = item;

      const showBetshareButton =
        isBetshareEnabled && basketMode === BasketModes.SIMPLE && status === BasketItemStatuses.VALIDATED;

      return {
        basketMode,
        outcomeLabel: outcomeLabel,
        betTitle: bet?.betTitle,
        previousOdd: bet?.previousOdd,
        nbItem,
        basketIsCashout,
        alertContent,
        showBetshareButton,
        sportId: match?.sportId || itemSportId,
        categoryId: match?.categoryId,
        title,
        isMobile: Target.__MOBILE__,
        isLive: MatchEntity.isLive(match),
        readOnly,
        cashout,
        banker,
        matchId: match?.matchId || itemMatchId,
        status,
        betslipId,
        value,
        changed,
        validatedValue,
        oddId,
        selectionItems,
        potentialGain,
        cardMode,
        eligibleFreebets,
        freebets,
        selectedFreebetId,
        isShowingMainBetConfirmation,
        isMyMatch: !!item.betbuilder,
        myMatchErrorLabel,
      };
    },
  );
};

export default makeBasketCardPropsSelector;
